// Splitting a section into the blocks that put a note right under its verses.
//
// The chapter page reads as one column: scripture, then (set off as an
// indented aside) the notes on it, then the next run of scripture. Rather
// than measuring rendered text and nudging notes into place, a section is
// cut at every verse a note starts on. Each cut is one block — a run of
// scripture followed by the notes that open on it — rendered in reading
// order, so no measurement or positioning is needed.

/** The part of a note this module needs; the view type carries more. */
export interface Pinned {
  startVerse: number | null;
  endVerse: number | null;
}

export interface AlignedRow<T extends Pinned> {
  /** The verses of scripture in this row, or null for a row with no passage. */
  startVerse: number | null;
  endVerse: number | null;
  notes: T[];
}

/**
 * Cut `[sectionStart, sectionEnd]` at each verse a note begins on. Notes that
 * start before the section, or name no verse at all, ride in the first row;
 * one that starts past the end rides in the last. Overlapping notes on the
 * same verse stay together, in the order they were written.
 */
export function alignRows<T extends Pinned>(
  sectionStart: number | null,
  sectionEnd: number | null,
  notes: readonly T[],
): AlignedRow<T>[] {
  if (sectionStart === null) return notes.length ? [{ startVerse: null, endVerse: null, notes: [...notes] }] : [];

  const end = Math.max(sectionEnd ?? sectionStart, sectionStart);
  const cuts = [sectionStart];
  for (const n of notes) {
    if (n.startVerse === null) continue;
    const at = Math.min(Math.max(n.startVerse, sectionStart), end);
    if (!cuts.includes(at)) cuts.push(at);
  }
  cuts.sort((a, b) => a - b);

  const rows: AlignedRow<T>[] = cuts.map((start, i) => ({
    startVerse: start,
    endVerse: (cuts[i + 1] ?? end + 1) - 1,
    notes: [],
  }));
  for (const n of notes) {
    const at = n.startVerse === null ? sectionStart : Math.min(Math.max(n.startVerse, sectionStart), end);
    // The last row that opens at or before this note.
    let i = 0;
    while (i + 1 < rows.length && rows[i + 1]!.startVerse! <= at) i++;
    rows[i]!.notes.push(n);
  }
  return rows;
}
